import type {
  SopReplayRunningStep,
  SopReplayStepView,
  SessionMessageEntry,
} from '@erp/rpc';
import {
  procedureKey,
  readSopTag,
  stepKey,
  visibleRunningStep,
} from './sopReplayCore';

export { sopStepsFromToolCalls, visibleRunningStep } from './sopReplayCore';

// ---------------------------------------------------------------------------
// Pure grouping logic (no React) — folds a flat agent trace into
// procedure → SOP step → the operator actions that step produced.
//
// A procedure replay reaches the trace as a flat run of low-level actions
// (goto / click / type) with no indication that a procedure is running or which
// of its steps is executing. The backend tags every row a replay produces with
// `sopStepId` / `sopStepIndex` / `sopProcedureName` / `sopProcedureOrdinal`, on
// the live session event AND on the persisted row's `inputData` — and
// `buildToolCallMessages` copies `inputData` into each message's `metadata`. So
// ONE function groups the live and the completed views alike.
// ---------------------------------------------------------------------------

/** One step, with the trace entries it produced. */
export interface SopStepGroup {
  step: SopReplayStepView;
  messages: SessionMessageEntry[];
}

/** One procedure replay: its steps in order, plus the step currently mid-replay (live runs only). */
export interface ProcedureGroup {
  kind: 'procedure';
  /** Display label, or null when neither the ai-service nor Node supplied one. Null rather than a
   * baked-in fallback: this module is deliberately React-free, so the renderer translates it. */
  name: string | null;
  /** 1-based within the run, so two replays of the SAME procedure stay separate groups. */
  ordinal: number;
  steps: SopStepGroup[];
  runningStep: SopReplayRunningStep | null;
}

/**
 * A run of consecutive entries produced by ONE executor — the agent's own free-form work, bounded.
 *
 * One agent run routinely hands the browser between executors: the remote-desktop login node
 * replays a login procedure, then `remote_desktop_execute` drives the app inside the desktop
 * agentically. They share the managed session, so their rows interleave into one thread and the
 * agentic work reads as a continuation of the procedure above it. The backend stamps each row with
 * the executor that produced it; this is the fold that turns those stamps into a boundary.
 */
export interface PhaseGroup {
  kind: 'phase';
  /** The backend's stable phase id (`TracePhase`). Display text is the renderer's business — this
   * module stays React-free, so it never resolves a label. */
  phase: string;
  messages: SessionMessageEntry[];
}

/** A trace entry that belongs to no procedure and carries no phase — renders exactly as it always
 * has. Every run recorded before phase stamping existed is entirely made of these. */
export interface UngroupedEntry {
  kind: 'message';
  message: SessionMessageEntry;
}

export type TraceGroup = ProcedureGroup | PhaseGroup | UngroupedEntry;

/** Must match `TRACE_PHASE_KEY` in backend/src/core/services/operator/tracePhase.ts. Rides the live
 * session event's metadata and the persisted row's `inputData` alike, so one read serves both. */
const TRACE_PHASE_KEY = 'tracePhase';

/** Which executor produced this row, or null on a row that carries no stamp. */
function readPhase(
  metadata: Record<string, unknown> | undefined,
): string | null {
  const phase = metadata?.[TRACE_PHASE_KEY];
  return typeof phase === 'string' && phase.trim() ? phase : null;
}

/**
 * Bucket consecutive same-phase entries into `PhaseGroup`s — the last pass, applied after procedure
 * grouping so a replay's own rows are already inside their card and never get re-homed here.
 *
 * Runs are CONSECUTIVE by design: a procedure card or an unstamped row between two same-phase runs
 * genuinely separates them in time, and merging across it would claim a continuity the trace
 * doesn't have.
 *
 * A lone phase covering the whole thread is unwrapped again. One card wrapping everything is pure
 * chrome — it draws a boundary where there is nothing on the other side of it — so an ordinary
 * single-executor run stays as flat as it was before any of this existed.
 */
function groupByPhase(groups: TraceGroup[]): TraceGroup[] {
  const out: TraceGroup[] = [];
  let phaseCount = 0;
  let hasProcedure = false;

  for (const entry of groups) {
    if (entry.kind !== 'message') {
      hasProcedure = hasProcedure || entry.kind === 'procedure';
      out.push(entry);
      continue;
    }
    const phase = readPhase(entry.message.metadata);
    if (phase === null) {
      out.push(entry);
      continue;
    }
    // The tail IS the open run — so anything pushed since (a procedure card, an unstamped row) has
    // already closed it, with no separate bookkeeping to keep honest.
    const tail = out[out.length - 1];
    if (tail?.kind === 'phase' && tail.phase === phase) {
      tail.messages.push(entry.message);
      continue;
    }
    out.push({ kind: 'phase', phase, messages: [entry.message] });
    phaseCount += 1;
  }

  if (hasProcedure || phaseCount > 1) return out;
  return out.flatMap((entry) =>
    entry.kind === 'phase'
      ? entry.messages.map((message) => ({
          kind: 'message' as const,
          message,
        }))
      : [entry],
  );
}

/** One step's full identity: its procedure ordinal and its own key. The single place the composite
 * is built, so the message loop and the step index can't drift apart on it. */
function groupKey(
  procedure: { name: string; ordinal: number } | null | undefined,
  stepId: string | null | undefined,
  index: number | null | undefined,
): string {
  return `${procedureKey(procedure?.ordinal ?? 1)}|${stepKey(stepId, index)}`;
}

/** A stand-in step for actions that have streamed but whose boundary hasn't arrived yet, so a
 * mid-replay step's work is visible immediately instead of vanishing until it resolves. */
function placeholderStep(
  tag: { stepId: string | null; stepIndex: number | null },
  procedure: { name: string; ordinal: number } | null,
): SopReplayStepView {
  return {
    index: tag.stepIndex ?? 0,
    name: '',
    mode: 'deterministic',
    codeKind: null,
    stepId: tag.stepId,
    error: null,
    verify: null,
    noop: false,
    procedure,
    // Unknown until the boundary lands; `stepOwner` falls back to the tag on this step's own
    // actions, which is what makes the seams visible mid-replay rather than only after a refresh.
    owner: null,
  };
}

/**
 * Which composed SOP a step came from, or null when the plan wasn't composed.
 *
 * Two sources, because the boundary and the actions arrive by different routes. The boundary row
 * carries `owner` once persisted — but LIVE, a boundary is validated against the generated wire
 * schema and so cannot carry a Node-added field, while an action row's tag can. Reading either
 * means a live composed replay shows its seams instead of waiting for a page refresh.
 */
export function stepOwner(group: SopStepGroup): string | null {
  if (group.step.owner) return group.step.owner;
  for (const message of group.messages) {
    const owner = message.metadata?.sopOwnerName;
    if (typeof owner === 'string' && owner) return owner;
  }
  return null;
}

/** One contiguous run of steps from the same composed SOP. */
export interface SopStepSegment {
  owner: string | null;
  steps: SopStepGroup[];
}

/**
 * Split a procedure's steps into per-SOP segments, or return null when there is nothing to split.
 *
 * Null on a single-owner plan — which is every uncomposed procedure, i.e. almost all of them — so
 * the renderer draws its flat step list exactly as before and this feature is invisible until a
 * plan actually composes.
 *
 * Contiguous runs, not one segment per distinct SOP: the same child embedded twice is two separate
 * passes through it, and merging them would put steps 2 and 9 under one header as though they ran
 * together.
 */
export function sopStepSegments(
  steps: SopStepGroup[],
): SopStepSegment[] | null {
  const segments: SopStepSegment[] = [];
  for (const group of steps) {
    const owner = stepOwner(group);
    const tail = segments[segments.length - 1];
    if (tail && tail.owner === owner) tail.steps.push(group);
    else segments.push({ owner, steps: [group] });
  }
  return segments.length > 1 ? segments : null;
}

/**
 * Fold a flat trace into procedure groups, preserving chronology.
 *
 * A procedure group takes the position of its FIRST entry, so the agent's work before and after a
 * replay still reads in order. Entries with no step tag pass through untouched — which is why a run
 * that never replayed a procedure renders exactly as it did before this grouping existed.
 *
 * A step declared in `steps` but with no messages of its own still gets a row (a branch node, or a
 * step that failed before acting): it was visible while the run was live, so it must not vanish.
 */
export function groupTraceBySopStep(
  messages: SessionMessageEntry[],
  steps: SopReplayStepView[],
  running?: SopReplayRunningStep | null,
): TraceGroup[] {
  const visibleRunning = visibleRunningStep(steps, running);
  // Nothing to group. Checked against the MESSAGES too, not just `steps`: mid-replay (and on a run
  // whose boundary rows never landed) the tagged actions arrive before any step is known, and
  // bailing out here would leave them stranded at the top level.
  if (!visibleRunning && !hasSopReplay(messages, steps)) {
    // Still phase-grouped: a run with no procedure in it can STILL hand the browser between
    // executors (the hardcoded remote-desktop login, then the task work), which is a boundary worth
    // drawing. `groupByPhase` unwraps itself when there's only one executor, so a plain agentic run
    // is untouched.
    return groupByPhase(
      messages.map((message) => ({ kind: 'message', message })),
    );
  }

  // Every step we know about, indexed by procedure then step, so a message can find its home in
  // one lookup and declared-but-silent steps still get rendered.
  const groups = new Map<string, ProcedureGroup>();
  const stepsByKey = new Map<string, SopStepGroup>();
  const out: TraceGroup[] = [];

  function procedureGroup(
    name: string | null,
    ordinal: number,
  ): ProcedureGroup {
    const key = procedureKey(ordinal);
    const existing = groups.get(key);
    if (existing) {
      // The name is display-only and only SOME contributors carry it (a wire boundary has none on
      // the run_sop path; Node's tagged action rows do). First non-null wins, so the card shows the
      // real title whichever side supplies it.
      existing.name ??= name;
      return existing;
    }
    const created: ProcedureGroup = {
      kind: 'procedure',
      name,
      ordinal,
      steps: [],
      runningStep: null,
    };
    groups.set(key, created);
    // First appearance fixes the group's place in the timeline.
    out.push(created);
    return created;
  }

  /** `ordinal` is passed explicitly by the message loop: a tag can carry an ordinal without a name,
   * and `step.procedure` is null in exactly that case, so deriving it from the step alone would
   * re-home a second replay's steps onto the first. */
  function stepGroup(
    step: SopReplayStepView,
    ordinal: number = step.procedure?.ordinal ?? 1,
  ): SopStepGroup {
    const key = `${procedureKey(ordinal)}|${stepKey(step.stepId, step.index)}`;
    const existing = stepsByKey.get(key);
    if (existing) return existing;
    const created: SopStepGroup = { step, messages: [] };
    stepsByKey.set(key, created);
    procedureGroup(step.procedure?.name ?? null, ordinal).steps.push(created);
    return created;
  }

  // Suppressing a boundary is only safe when a step row will render it instead. A caller that
  // supplies no steps (SopReplayViewer hands the viewer one step's tool calls, already grouped by
  // its own card) has nothing else to show them with, and dropping them there would blank the
  // panel rather than de-duplicate it.
  // The running step counts: at the very start of a replay nothing has resolved yet, and without
  // it the first "Step 1 — running" bubble would show until the step completed and then vanish.
  const stepsWillRender = steps.length > 0 || visibleRunning !== null;

  for (const message of messages) {
    // The boundary row IS the step: live it drives `sopReplay.steps`, and on a finished run
    // `sopStepsFromToolCalls` reads exactly these rows to rebuild the step headers. Rendering it
    // as a message too repeats every step — twice over, live, once per boundary kind.
    if (stepsWillRender && isBoundaryProjection(message.metadata)) continue;
    const tag = readSopTag(message.metadata);
    if (!tag) {
      out.push({ kind: 'message', message });
      continue;
    }
    const tagProcedure = tag.procedureName
      ? { name: tag.procedureName, ordinal: tag.procedureOrdinal }
      : null;
    // Seed/label the procedure group from the tag before resolving the step. A tagged action row is
    // often the ONLY side that knows the procedure's title (the run_sop wire boundary carries none),
    // and doing it here also fixes the group's place in the timeline at its first entry.
    procedureGroup(tag.procedureName, tag.procedureOrdinal);
    // Keyed off the tag's own ordinal, not `tagProcedure` — the latter is null whenever the name is
    // missing, which would silently re-home a second replay's actions onto the first.
    const key = `${procedureKey(tag.procedureOrdinal)}|${stepKey(tag.stepId, tag.stepIndex)}`;
    const known = stepsByKey.get(key);
    if (known) {
      known.messages.push(message);
      continue;
    }
    // A tagged message whose step boundary hasn't arrived yet (mid-replay, live). Seed a
    // placeholder step from the tag so its actions are visible immediately rather than
    // disappearing until the step resolves.
    const declared = steps.find(
      (s) => groupKey(s.procedure, s.stepId, s.index) === key,
    );
    stepGroup(
      declared ?? placeholderStep(tag, tagProcedure),
      tag.procedureOrdinal,
    ).messages.push(message);
  }

  // Declared steps that produced no messages of their own.
  for (const step of steps) stepGroup(step);

  if (visibleRunning) {
    procedureGroup(
      visibleRunning.procedure?.name ?? null,
      visibleRunning.procedure?.ordinal ?? 1,
    ).runningStep = visibleRunning;
  }

  // Steps arrive in replay order on the live path, but a completed run's messages can seed them
  // out of order (a placeholder before its boundary), so sort each group by index.
  for (const group of groups.values()) {
    group.steps.sort((a, b) => a.step.index - b.step.index);
  }
  // Last: bound whatever stayed at the top level by the executor that produced it. Runs after
  // procedure grouping, so a replay's rows are already inside their card and can't be re-homed.
  return groupByPhase(out);
}

/** The replay boundaries a live event announces itself with. Mirrors `isSopReplayBoundary` in
 * backend/src/core/services/operator/sopReplayStepTrace.ts, minus `sop_replay_branch` — a branch
 * decision names the path the run took, which no step row repeats. */
const LIVE_STEP_BOUNDARY_KINDS = new Set([
  'sop_replay_step',
  'sop_replay_step_started',
]);

/**
 * Is this message a step boundary rather than something that happened during the step?
 *
 * A boundary is a projection of the step itself, and the procedure card already draws it as a row
 * with the index, name and mode badge. Rendering it as a message too restates every step — twice
 * live (once on start, once on completion), which is what turned an 11-step replay into 22 extra
 * "Step N: …" rows below the card that already listed them.
 *
 * Two shapes reach here, because the thread accumulates from more than one source:
 *   - LIVE — the event's own `kind`, straight off the wire signal, via either the AG-UI stream or
 *     the session poll. The poll is why suppressing the bubble server-side isn't sufficient: those
 *     messages never pass through the AG-UI adapter.
 *   - PERSISTED — a completed run's boundary row, discriminated by `sopStepName`, which only the
 *     boundary writer stamps (`persistStepBoundaryRows`); an action row carries the tag and its own
 *     args instead. Reading the stored shape rather than a new flag de-duplicates runs recorded
 *     before this change too.
 */
function isBoundaryProjection(
  metadata: Record<string, unknown> | undefined,
): boolean {
  if (typeof metadata?.sopStepName === 'string') return true;
  return (
    typeof metadata?.kind === 'string' &&
    LIVE_STEP_BOUNDARY_KINDS.has(metadata.kind)
  );
}

/** Does this trace contain any procedure replay at all? Lets a caller skip the grouped renderer
 * entirely — and guarantees an ordinary agentic run is untouched by it. */
export function hasSopReplay(
  messages: SessionMessageEntry[],
  steps: SopReplayStepView[],
): boolean {
  return (
    steps.length > 0 || messages.some((m) => readSopTag(m.metadata) !== null)
  );
}
